import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatValue } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import PlayerBadge from "@/components/PlayerBadge";
import PlayerHeadshot, { TeamPill, PosRank } from "@/components/PlayerHeadshot";
import { Loader2, Search, X } from "lucide-react";

const FORMATS = [{ id: "1qb", label: "1QB" }, { id: "superflex", label: "Superflex" }, { id: "tep", label: "TE Premium" }];
const POSITIONS = ["ALL", "QB", "RB", "WR", "TE"];

export default function PublicPlayers() {
  const [params] = useSearchParams();
  const initialPos = (params.get("pos") || "ALL").toUpperCase();
  const [fmt, setFmt] = useState("superflex");
  const [pos, setPos] = useState(POSITIONS.includes(initialPos) ? initialPos : "ALL");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { const t = setTimeout(() => setDebounced(query.trim()), 250); return () => clearTimeout(t); }, [query]);
  useEffect(() => {
    setLoading(true);
    api.get("/public/players", { params: { format: fmt, position: pos, q: debounced || undefined, limit: 800 } })
      .then(r => setData(r.data)).finally(() => setLoading(false));
  }, [fmt, pos, debounced]);

  const rows = data?.players || [];
  const title = "Dynasty Fantasy Football Player Values & Rankings | Dynasty Metrics";
  const desc = "Free dynasty fantasy football trade values for every player — proprietary, age-adjusted DME rankings for 1QB, Superflex and TE-Premium leagues. Updated daily.";

  return (
    <PublicShell>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href="https://dynastymetrics.com/players" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content="https://dynastymetrics.com/players" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <header className="mb-6">
        <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// DME PLAYER VALUES</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight" data-testid="public-players-h1">Dynasty Player Values</h1>
        <p className="text-[var(--text-2)] mt-2 max-w-2xl">
          Proprietary, age-adjusted dynasty trade values for every NFL player — re-priced daily. Tap any player for the full breakdown.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
          <input data-testid="public-players-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search a player…" className="input-dark w-full !pl-10 pr-9" />
          {query && <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex items-center gap-1 border border-[var(--border)] rounded-sm p-0.5">
          {FORMATS.map(f => (
            <button key={f.id} onClick={() => setFmt(f.id)} data-testid={`public-fmt-${f.id}`}
              className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${fmt === f.id ? "bg-[var(--accent)] text-black font-semibold" : "text-[var(--text-2)] hover:text-white"}`}>{f.label}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {POSITIONS.map(p => (
          <button key={p} onClick={() => setPos(p)} className={`text-xs px-3 py-1 rounded-sm border transition-colors ${pos === p ? "bg-[var(--accent)] text-black font-semibold border-[var(--accent)]" : "text-[var(--text-2)] border-[var(--border)] hover:text-white"}`}>{p}</button>
        ))}
        <span className="ml-auto text-[10px] tracking-widest text-[var(--text-3)] font-mono">{rows.length} PLAYERS</span>
      </div>

      {loading ? (
        <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>
      ) : (
        <div className="card-bordered overflow-hidden" data-testid="public-players-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] tracking-[0.15em] text-[var(--text-3)] font-mono border-b border-[var(--border)]">
            <div className="col-span-1">#</div><div className="col-span-7">PLAYER</div><div className="col-span-4 text-right text-[var(--accent)]">DME VALUE</div>
          </div>
          {rows.map(p => (
            <Link key={p.slug} to={`/players/${p.slug}`} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center text-sm border-b border-[var(--border)] last:border-0 hover:bg-white/5" data-testid={`public-row-${p.slug}`}>
              <div className="col-span-1 font-mono text-[var(--text-3)]">{p.rank}</div>
              <div className="col-span-7 min-w-0 flex items-center gap-2.5">
                <PlayerHeadshot playerId={p.player_id} name={p.name} team={p.team} size={36} />
                <div className="min-w-0">
                  <div className="truncate hover:text-[var(--accent)]">{p.name}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <PosRank rank={p.pos_rank} onFilter={setPos} />
                    <TeamPill team={p.team} />
                    <span className="text-[10px] tracking-widest text-[var(--text-3)]">{p.position}{p.age ? ` · ${p.age}y` : ""}</span>
                  </div>
                </div>
              </div>
              <div className="col-span-4 text-right font-mono text-[var(--accent)]">{formatValue(p.dme)}</div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-8 card-bordered p-6 text-center">
        <div className="font-display text-xl font-bold mb-1">Build the perfect trade.</div>
        <p className="text-sm text-[var(--text-2)] mb-4">Free trade calculator, power rankings & multi-league portfolio — synced live with Sleeper.</p>
        <Link to="/login" className="btn-primary inline-flex" data-testid="public-players-cta">Get Started Free</Link>
      </div>
    </PublicShell>
  );
}
